import type { FirebaseApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import { STORAGE_KEYS } from '../../constants';
import { withUid } from '../../utils/helpers';
import { logAuthLifecycle } from '../logging/secureLogger';
import { purgeThreadStorageForSessionReset } from '../../components/GlobalChatSystem/threadPersist';
import { claimVisitorInheritanceOnce, markHasSignedIn } from '../../services/guestChat/client';

// Site-chat thread-switcher instance id (literal; see usePageChatConfig.ts).
const SITE_CHAT_INSTANCE_ID = 'site_chat';
const GLOBAL_CHAT_INSTANCE_ID = 'global_chat';

// Central registry for Firestore listeners so we can reliably unsubscribe on session reset
type Unsubscribe = () => void;
const firestoreListenerRegistry: Set<Unsubscribe> = new Set();

export function registerFirestoreListener(unsubscribe: Unsubscribe): Unsubscribe {
  firestoreListenerRegistry.add(unsubscribe);
  return () => {
    try { unsubscribe(); } finally { firestoreListenerRegistry.delete(unsubscribe); }
  };
}

// Optional store reset callbacks (Redux/Zustand/Contexts can register here)
type StoreResetCallback = () => void;
const storeResetCallbacks: Set<StoreResetCallback> = new Set();

export function registerStoreResetCallback(cb: StoreResetCallback): () => void {
  storeResetCallbacks.add(cb);
  return () => { storeResetCallbacks.delete(cb); };
}

// Structured logger (no raw emails)
function logLifecycle(event: string, data?: Record<string, unknown>): void {
  try {
    console.info('auth.lifecycle', { event, ...(data || {}) });
  } catch {}
}

let currentUid: string | null = null;
let authObserverUnsub: Unsubscribe | null = null;

export async function resetSession(
  reason: string = 'manual',
  departingUid?: string | null,
): Promise<void> {
  logLifecycle('reset_session_begin', { reason });
  try { logAuthLifecycle('reset_session_begin', currentUid, { reason }); } catch {}

  // 1) Unsubscribe all Firestore listeners
  try {
    const pending: Array<void> = [];
    firestoreListenerRegistry.forEach((unsub) => {
      try { unsub(); } catch {}
    });
  } finally {
    firestoreListenerRegistry.clear();
  }

  // 2) Clear per-user client stores via registered callbacks
  try {
    storeResetCallbacks.forEach((cb) => { try { cb(); } catch {} });
  } catch {}

  // 3) Clear per-user caches in localStorage (but keep required auth persistence intact)
  // If we know the current uid, prefer removing namespaced keys.
  // Callers on the logout/uid-change path pass the departing uid explicitly because
  // the auth observer nulls `currentUid` BEFORE invoking resetSession.
  const uid = (typeof departingUid === 'string' && departingUid.trim())
    ? departingUid.trim()
    : currentUid;

  // 3a) Authoritative, lifecycle-independent thread-storage purge (#355 logout-instant).
  // Runs synchronously here so the departing user's chat-thread residue (legacy +
  // uid-scoped metadata + message transcripts) is wiped AT the logout instant,
  // regardless of whether GlobalChatSystem/useChatThreads is still mounted to catch
  // the app:session_reset event below. The in-hook listener stays as belt-and-suspenders.
  //
  // Round P guard: 'no_user' fires on EVERY signed-out page boot (the auth
  // observer's !user branch with no prior uid in this page's lifetime), where
  // NOTHING departed — purging there destroys the GUEST's unscoped thread
  // history (repro: guest converses → sign-in link to /auth → back/refresh →
  // history gone; /auth boots auth before the chat hydrates, so the purge
  // wins the race that chat pages mask). Purge only on a real identity
  // boundary (sign_out / uid_changed / explicit_logout / manual).
  if (reason !== 'no_user') {
    // INCIDENT-20260822: sign-out purged the ONLY copy of messages whose
    // debounced server writes were still pending (or had silently failed) —
    // the purge destroyed local truth and sign-in rebuilt the thread from a
    // stale server transcript. Flush every pending debounced write and WAIT
    // for the real outcomes (bounded — sign-out must never hang) before
    // touching local storage.
    try {
      const { debounceManager } = await import('../../repositories/firestoreUtils');
      await Promise.race([
        debounceManager.flushAll(),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch { /* flushing is best-effort; purge must still run */ }
    try { purgeThreadStorageForSessionReset(SITE_CHAT_INSTANCE_ID, uid); } catch {}
    // 2026-08-21 sign-out leak: the landing page and DEFAULT_CHAT_CONFIG
    // mount the chat as 'global_chat' — purging only 'site_chat' left that
    // whole namespace (and its IDB mirrors) intact across sign-out.
    try { purgeThreadStorageForSessionReset(GLOBAL_CHAT_INSTANCE_ID, uid); } catch {}
    // Returning-tab view markers (2026-09-01) ride the same identity sweep:
    // the departing uid's persisted scroll anchors go with its threads
    // (message ids only, but a departed identity leaves nothing behind).
    // Guest markers survive by design — guest-history protection.
    if (uid) {
      try {
        const { clearThreadViewMarkersForUid } = await import('../threadScrollRestore');
        clearThreadViewMarkersForUid(uid);
      } catch {}
    }
  }
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.USER_PROFILE) : STORAGE_KEYS.USER_PROFILE); } catch {}
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.PROFILE_DATA) : STORAGE_KEYS.PROFILE_DATA); } catch {}
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.VERSIONS) : STORAGE_KEYS.VERSIONS); } catch {}
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.SELECTED_VERSION) : STORAGE_KEYS.SELECTED_VERSION); } catch {}
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.EXPANDED_SECTIONS) : STORAGE_KEYS.EXPANDED_SECTIONS); } catch {}
  // Retired setting (global autonomy removed 2026-08); keep purging the stale key for one release
  try { localStorage.removeItem(uid ? withUid(uid, 'global_autonomy_level') : 'global_autonomy_level'); } catch {}
  try { localStorage.removeItem(uid ? withUid(uid, STORAGE_KEYS.FLOATING_CONTROLS_POSITION) : STORAGE_KEYS.FLOATING_CONTROLS_POSITION); } catch {}

  // Clear any account-scoped notifications caches to avoid cross-account bleed
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('notifications_') || key.startsWith('notifications_read_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
  } catch {}

  // 3b) Durability audit rank #6 + #13 (2026-07-13): unscoped account-tainted
  // residue that previously survived sign-out AND no-logout account switches.
  //  - prest0_global_chat_history(+_owner): the hydrate path UPLOADS this to
  //    the next signed-in uid's Firestore when their copy is empty — the
  //    confirmed cross-account transcript leak.
  //  - prest0_dr_workflow_state(+_owner): deep-research clarifying checkpoint;
  //    restored into ANY signed-in uid's runtime, and its presence blocks the
  //    new account's own Firestore checkpoint sync-down.
  //  - prest0_workflow_outputs / prest0_document_analyses: workflowOutputStore
  //    mirrors — account-content caches consumed by button-click context
  //    passing.
  // All are safe to drop at any session boundary: cloud copies are the
  // authority for the owning account.
  for (const residueKey of [
    'prest0_global_chat_history',
    'prest0_global_chat_history_owner',
    // 2026-08-21 sign-out leak: the unscoped composer draft carried the
    // departed member's typed text into the next identity's composer.
    'prest0_global_chat_draft',
    'prest0_dr_workflow_state',
    'prest0_dr_workflow_state_owner',
    'prest0_workflow_outputs',
    'prest0_document_analyses',
  ]) {
    try { localStorage.removeItem(residueKey); } catch {}
  }

  // 4) Broadcast reset so UI can react (navigate to unauthenticated views etc.)
  try {
    window.dispatchEvent(new CustomEvent('app:session_reset', { detail: { reason } }));
  } catch {}

  logLifecycle('reset_session_complete', { reason });
  try { logAuthLifecycle('reset_session_complete', currentUid, { reason }); } catch {}
}

function emitAuthState(user: User | null): void {
  try {
    const payload = user ? { uid: user.uid, isAnonymous: !!user.isAnonymous } : null;
    window.dispatchEvent(new CustomEvent('app:auth', { detail: { user: payload } }));
  } catch {}
}

export async function setupAuthObserver(app: FirebaseApp): Promise<void> {
  if (authObserverUnsub) {
    logLifecycle('observer_already_initialized');
    return;
  }

  // Where the signed-in user is kept (local storage, then the browser's
  // database, then the per-tab session store) is stated once, in
  // utils/firebase.ts, where this instance is created. It is never changed
  // here: a setPersistence call on boot moved the stored user out of local
  // storage and back, and every other open tab signed out in between
  // (2026-09-23, tests/auth/authStoreOrder.test.ts).
  const auth = getAuth(app);

  authObserverUnsub = onAuthStateChanged(auth, async (user) => {
    try {
      emitAuthState(user);
      if (!user) {
        // Fully signed out → clear session state
        const prevUid = currentUid;
        currentUid = null;
        await resetSession(prevUid ? 'sign_out' : 'no_user', prevUid);
        logLifecycle('signed_out');
        try { logAuthLifecycle('signed_out', prevUid, {}); } catch {}
        return;
      }

      // If UID changed across sessions, perform a reset before proceeding
      if (currentUid && currentUid !== user.uid) {
        await resetSession('uid_changed', currentUid);
        try { logAuthLifecycle('uid_changed', currentUid, { nextUid: user.uid }); } catch {}
      }

      currentUid = user.uid;
      logLifecycle('signed_in', { uid: user.uid, isAnonymous: !!user.isAnonymous });
      try { logAuthLifecycle('signed_in', user.uid, { isAnonymous: !!user.isAnonymous }); } catch {}

      // G1 guest device memory: this device has completed a real sign-in.
      // Stored OUTSIDE the purged thread/session namespaces so it survives
      // sign-out — the guest chat charter uses it to offer "sign back in"
      // directly instead of conditional phrasing.
      if (!user.isAnonymous) {
        try { markHasSignedIn(); } catch {}
        // D13-5 (owner ruling 2026-08-24): inherit any pre-account visitor
        // memory into this account. Fire-and-forget — the server side is
        // first-claim-wins and idempotent, the caller self-throttles via a
        // per-uid marker, and nothing here can block or fail sign-in.
        try { void claimVisitorInheritanceOnce(user); } catch {}
      }

      // Canonical flow ensures mapping via server; no client-side mapping.
      // On sign-in, downstream code must re-resolve the canonical ownerUid and reload user state
      // from users/{ownerUid}, overriding any local cache.

      // Kick off fresh data load keyed by UID (downstream modules listen for this event)
      try { window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: user.uid } })); } catch {}
    } catch (err) {
      logLifecycle('observer_error', { message: (err as any)?.message || String(err) });
    }
  });
}

// Convenience subscription helper for modules that want auth state without touching Firebase directly
export function subscribeToAuthState(handler: (user: { uid: string; isAnonymous: boolean } | null) => void): Unsubscribe {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent).detail as { user: { uid: string; isAnonymous: boolean } | null };
    handler(detail ? detail.user : null);
  };
  window.addEventListener('app:auth', listener as EventListener);
  return () => window.removeEventListener('app:auth', listener as EventListener);
}


